import React, { useEffect, useState } from "react";
import instance2 from "../../../api/axiosInstance2";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import { navigate, Link } from "gatsby";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import {
    container,
    sectionHeader,
    sectionHeaderTitle,
    sectionHeaderDescription,
    sectionHeaderButton,
    looksGrid,
    lookCard,
    lookThumb,
    lookThumbGrid,
    lookThumbImg,
    lookThumbPlaceholder,
    lookMeta,
    lookDate,
    lookTag,
    info,
    error,
    accountContent,
    tryOnPanel,
    tryOnHeading,
    tryOnText,
    tryOnProducts,
    tryOnProduct,
    tryOnProductImage,
    tryOnProductName,
    tryOnButton,
    tryOnNotice,
    tryOnResult,
} from "./stylizations.module.scss";
import { mainGrid } from "../../../styles/grid.module.scss";
import { TStatus } from "../../../models/status.model";
import { IStylization } from "../../../models/stylization.model";
import { getAbsolutePath } from "../../utility/routes.utility";
import {
    selectStylizationsItems,
    selectStylizationsStatus,
} from "../../../redux/stylizations/stylizations.selectors";
import {
    clearStylizationsAction,
    getStylizationsAction,
} from "../../../redux/stylizations/stylizations.actions";
import { clearSingleStylizationAction } from "../../../redux/single-stylization/single-stylization.actions";
import { selectSingleStylizationStatus } from "../../../redux/single-stylization/single-stylization.selectors";
import { normalizeApiFileUrl } from "../../../utils/api-url";
import endpoints from "../../../config/endpoints";
import useUser from "../../auth/hooks/use-user";

import Loader from "../../../components/atoms/loader";
import AccountEmptyState from "../molecules/account-empty-state";
import emptyStylizationsImage from "../../../assets/images/stylization_generator_bg.png";

const Stylizations: React.FC = () => {
    const { t } = useI18next();
    const dispatch = useDispatch();
    const status: TStatus = useSelector(selectStylizationsStatus);
    const singleStylizationStatus: TStatus = useSelector(selectSingleStylizationStatus);
    const stylizations: IStylization[] = useSelector(selectStylizationsItems);
    const { profile } = useUser();
    const [tryOnLoadingKey, setTryOnLoadingKey] = useState<string | null>(null);
    const [tryOnResultUrl, setTryOnResultUrl] = useState<string | null>(null);
    const [tryOnMessage, setTryOnMessage] = useState<string | null>(null);

    const showLoader =
        status === "loading" || status === "idle" || singleStylizationStatus === "loading";

    const handleAddStylization = async () => {
        await navigate(getAbsolutePath("GENERATOR_NEW_STYLIZATION"));
    };

    const handleTryOn = async (stylization: IStylization, productId: number) => {
        const key = `${stylization.stylizationId}-${productId}`;
        setTryOnLoadingKey(key);
        setTryOnMessage(null);
        if (tryOnResultUrl) {
            URL.revokeObjectURL(tryOnResultUrl);
            setTryOnResultUrl(null);
        }
        try {
            const response = await instance2.post(
                endpoints.virtualTryOnProfile.create,
                { stylizationId: stylization.stylizationId, productId },
                { withCredentials: true, responseType: "blob", timeout: 310000 }
            );
            setTryOnResultUrl(URL.createObjectURL(response.data));
        } catch {
            setTryOnMessage("Nie udało się wykonać przymiarki. Sprawdź zdjęcie profilowe i spróbuj ponownie.");
        } finally {
            setTryOnLoadingKey(null);
        }
    };

    useEffect(() => {
        return () => {
            if (tryOnResultUrl) {
                URL.revokeObjectURL(tryOnResultUrl);
            }
        };
    }, [tryOnResultUrl]);

    useEffect(() => {
        if (singleStylizationStatus === "success") {
            dispatch(clearStylizationsAction());
            dispatch(getStylizationsAction());
        }
    }, [singleStylizationStatus]);

    useEffect(() => {
        dispatch(getStylizationsAction());
        return () => {
            dispatch(clearStylizationsAction());
            dispatch(clearSingleStylizationAction());
        };
    }, [dispatch]);

    const getStylizationImages = (stylization: IStylization): string[] => {
        const images: string[] = [];
        if (stylization.products && stylization.products.length > 0) {
            stylization.products.forEach(product => {
                if (product.imgUrl) {
                    images.push(normalizeApiFileUrl(product.imgUrl));
                }
            });
        }
        if (images.length === 0 && stylization.imgUrl) {
            images.push(normalizeApiFileUrl(stylization.imgUrl));
        }
        return Array.from(new Set(images.filter(Boolean))).slice(0, 3);
    };

    const getStylizationTag = (stylization: IStylization): string => {
        if (stylization.occasion && stylization.occasion.label) {
            return stylization.occasion.label;
        }
        if (stylization.name) {
            return stylization.name;
        }
        return "";
    };

    return (
        <div className={`${mainGrid} ${container}`}>
            <div className={accountContent}>
                {showLoader && <Loader position="absolute" />}
                
                <div className={sectionHeader}>
                    <div>
                        <h1 className={sectionHeaderTitle}>Twoje stylizacje</h1>
                        <p className={sectionHeaderDescription}>
                            Przeglądaj zapisane zestawy ubrań i wracaj do ulubionych.
                        </p>
                    </div>
                    <button 
                        className={sectionHeaderButton} 
                        type="button"
                        onClick={handleAddStylization}
                    >
                        Dodaj stylizację
                    </button>
                </div>

                {status === "fail" && (
                    <p className={`${info} ${error}`}>{t("stylizations.fail")}</p>
                )}
                {status === "success" && stylizations.length === 0 && (
                    <AccountEmptyState
                        image={emptyStylizationsImage}
                        title="Tu pojawią się Twoje stylizacje"
                    >
                        Stwórz pierwszą stylizację, a zapisany zestaw znajdziesz później właśnie tutaj.
                    </AccountEmptyState>
                )}
                {status === "success" && stylizations.length > 0 && (
                    <div className={looksGrid}>
                        {stylizations
                            .filter(stylization => stylization.autoSave == 0)
                            .map((stylization) => {
                                const imageUrls = getStylizationImages(stylization);
                                const tag = getStylizationTag(stylization);
                                const date = moment(stylization.createdAt * 1000).format('DD.MM.YYYY');
                                
                                return (
                                    <Link
                                        key={stylization.stylizationId}
                                        to={`${getAbsolutePath("GENERATOR_STYLIZATION")}/${stylization.stylizationId}`}
                                        className={lookCard}
                                    >
                                        <div className={lookThumb}>
                                            {imageUrls.length > 0 ? (
                                                <StylizationImages
                                                    imageUrls={imageUrls}
                                                    alt={stylization.name || "Stylizacja"}
                                                />
                                            ) : (
                                                <div className={lookThumbPlaceholder} />
                                            )}
                                        </div>
                                        <div className={lookMeta}>
                                            <span className={lookDate}>{date}</span>
                                            {tag && <span className={lookTag}>{tag}</span>}
                                        </div>
                                    </Link>
                                );
                            })}
                    </div>
                )}
                {status === "success" && stylizations.some(stylization => stylization.autoSave == 0) && (
                    <section className={tryOnPanel}>
                        <h2 className={tryOnHeading}>Przymierz na swoim zdjęciu</h2>
                        <p className={tryOnText}>
                            Wybierz ubranie z zapisanej stylizacji. Nałożymy je na Twoje zdjęcie profilowe.
                            Wynik zobaczysz tylko tutaj i nie zapisujemy go na koncie.
                        </p>
                        {!profile?.avatarUri && (
                            <p className={tryOnNotice}>
                                Najpierw dodaj zdjęcie profilowe w zakładce „Dane konta”.
                            </p>
                        )}
                        {stylizations.filter(stylization => stylization.autoSave == 0).map((stylization) => (
                            <div key={`try-on-${stylization.stylizationId}`} className={tryOnProducts}>
                                {stylization.products.filter(product => product.imgUrl).map((product) => {
                                    const key = `${stylization.stylizationId}-${product.productId}`;
                                    const loading = tryOnLoadingKey === key;
                                    return (
                                        <div className={tryOnProduct} key={key}>
                                            <img className={tryOnProductImage} src={normalizeApiFileUrl(product.imgUrl)} alt={product.brandName || "Element stylizacji"} />
                                            <span className={tryOnProductName}>{product.brandName || product.category || "Element stylizacji"}</span>
                                            <button
                                                type="button"
                                                className={tryOnButton}
                                                disabled={!profile?.avatarUri || !!tryOnLoadingKey}
                                                onClick={() => handleTryOn(stylization, product.productId)}
                                            >
                                                {loading ? "Przymierzanie..." : "Przymierz"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                        {tryOnMessage && <p className={tryOnNotice}>{tryOnMessage}</p>}
                        {tryOnResultUrl && <img className={tryOnResult} src={tryOnResultUrl} alt="Wynik wirtualnej przymiarki" />}
                    </section>
                )}
            </div>
        </div>
    );
};

export default Stylizations;

interface IStylizationImageProps {
    imageUrls: string[];
    alt: string;
}

function StylizationImages({ imageUrls, alt }: IStylizationImageProps) {
    return (
        <div className={lookThumbGrid} data-count={imageUrls.length}>
            {imageUrls.map((imageUrl, index) => (
                <StylizationImage
                    key={`${imageUrl}-${index}`}
                    imageUrl={imageUrl}
                    alt={alt}
                />
            ))}
        </div>
    );
}

function StylizationImage({ imageUrl, alt }: { imageUrl: string; alt: string }) {
    const [failed, setFailed] = useState(false);

    if (failed) {
        return <div className={lookThumbPlaceholder} />;
    }

    return (
        <div className={lookThumbPlaceholder}>
            <img
                className={lookThumbImg}
                src={imageUrl}
                alt={alt}
                onError={() => setFailed(true)}
            />
        </div>
    );
}
